import { CategoryTypes } from "../../../../interfaces/enums";

export interface DisplayData {
  text?: string;
  years?: string | null;
  hasStyledYears: boolean;
  participantsData?: Array<{
    name: string;
    year: number | null;
  }>;
}

/**
 * Generuje dane do wyświetlania dla różnych typów kategorii
 */
export const generateDisplayWithYears = (presentation: any, categoryType: string): DisplayData => {
  if (!presentation.participants || presentation.participants.length === 0) {
    return { text: presentation.name, hasStyledYears: false };
  }

  // Dla solo - tylko roczniki w nawiasach
  if (categoryType === CategoryTypes.SOLO) {
    const years = presentation.participants
      .map((participant: any) =>
        participant.birthDate ? new Date(participant.birthDate).getFullYear() : null,
      )
      .filter((year: number | null) => year !== null);

    return {
      text: presentation.name,
      years: years.length > 0 ? years.join(", ") : null,
      hasStyledYears: true,
    };
  }

  // Dla duet i trio - każde imię z indywidualnym rocznikiem
  if (categoryType === CategoryTypes.DUO || categoryType === CategoryTypes.TRIO) {
    const participantsData = presentation.participants.map((participant: any) => {
      const year = participant.birthDate ? new Date(participant.birthDate).getFullYear() : null;
      return {
        name: `${participant.firstName} ${participant.lastName}`,
        year: year,
      };
    });

    return {
      participantsData,
      hasStyledYears: true,
    };
  }

  return { text: presentation.name, hasStyledYears: false };
};

/**
 * Sprawdza czy należy pokazać skład prezentacji
 */
export const shouldShowComposition = (categoryType: string): boolean => {
  return categoryType === CategoryTypes.OTHER;
};

/**
 * Porównanie osób: najpierw po nazwisku (A->Z), potem po imieniu (A->Z)
 */
function comparePersonsByLastThenFirst(a: any, b: any): number {
  const lnA = String(a?.lastName || "");
  const lnB = String(b?.lastName || "");
  const byLast = lnA.localeCompare(lnB, "pl", { sensitivity: "base" });
  if (byLast !== 0) return byLast;
  const fnA = String(a?.firstName || "");
  const fnB = String(b?.firstName || "");
  return fnA.localeCompare(fnB, "pl", { sensitivity: "base" });
}

/**
 * Sortuje prezentacje w zależności od typu kategorii.
 * - SOLO: po nazwisku (A->Z), następnie po imieniu (A->Z) jedynego uczestnika
 * - DUO/TRIO: najpierw sortuje skład lokalnie (A->Z po nazwisku, potem imieniu),
 *   następnie porządkuje prezentacje po pierwszym członku składu (jak w SOLO)
 * - OTHER: po nazwie (A->Z), a w razie remisu po tytule (A->Z)
 */
export function sortPresentations(presentations: any[], categoryType: string): any[] {
  const copy = [...(presentations || [])];

  if (categoryType === CategoryTypes.SOLO) {
    return copy.sort((a, b) => {
      const pa = Array.isArray(a?.participants) ? a.participants : [];
      const pb = Array.isArray(b?.participants) ? b.participants : [];
      const aMain = pa[0] || null;
      const bMain = pb[0] || null;
      const cmp = comparePersonsByLastThenFirst(aMain, bMain);
      if (cmp !== 0) return cmp;
      // Fallback dla stabilności
      const nameCmp = String(a?.name || "").localeCompare(String(b?.name || ""), "pl", {
        sensitivity: "base",
      });
      if (nameCmp !== 0) return nameCmp;
      return String(a?.title || "").localeCompare(String(b?.title || ""), "pl", {
        sensitivity: "base",
      });
    });
  }

  if (categoryType === CategoryTypes.DUO || categoryType === CategoryTypes.TRIO) {
    return copy.sort((a, b) => {
      const pa = Array.isArray(a?.participants) ? a.participants : [];
      const pb = Array.isArray(b?.participants) ? b.participants : [];
      // Posortuj lokalny skład (bez mutowania oryginału)
      const sortedA = [...pa].sort(comparePersonsByLastThenFirst);
      const sortedB = [...pb].sort(comparePersonsByLastThenFirst);
      const aMain = sortedA[0] || null;
      const bMain = sortedB[0] || null;
      const cmp = comparePersonsByLastThenFirst(aMain, bMain);
      if (cmp !== 0) return cmp;
      // Fallbacki na wypadek identycznego pierwszego członka
      const lenCmp = sortedA.length - sortedB.length;
      if (lenCmp !== 0) return lenCmp;
      const nameCmp = String(a?.name || "").localeCompare(String(b?.name || ""), "pl", {
        sensitivity: "base",
      });
      if (nameCmp !== 0) return nameCmp;
      return String(a?.title || "").localeCompare(String(b?.title || ""), "pl", {
        sensitivity: "base",
      });
    });
  }

  // OTHER
  return copy.sort((a, b) => {
    const byName = String(a?.name || "").localeCompare(String(b?.name || ""), "pl", {
      sensitivity: "base",
    });
    if (byName !== 0) return byName;
    return String(a?.title || "").localeCompare(String(b?.title || ""), "pl", {
      sensitivity: "base",
    });
  });
}
